import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, type LucideIcon } from 'lucide-react';
import { Banner } from '@/components/ui/Banner';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { Input } from '@/components/ui/Input';
import { Skeleton, SkeletonText } from '@/components/ui/Skeleton';
import { TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';

export function SettingsStack({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return <div className={cn('flex min-w-0 flex-col gap-s4', className)}>{children}</div>;
}

export function SettingsFormGrid({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('mt-s5 grid grid-cols-1 gap-s4 nav:grid-cols-2', className)}>{children}</div>
  );
}

export function SettingsFullField({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return <div className={cn('min-w-0 nav:col-span-2', className)}>{children}</div>;
}

export function SectionCard({
  title,
  subtitle,
  readOnly,
  action,
  children,
  className,
}: {
  title: string;
  subtitle?: string;
  readOnly?: boolean;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  const { t } = useTranslation();
  return (
    <Card className={cn('min-w-0', className)}>
      <div className="flex flex-wrap items-start gap-s3">
        <div className="min-w-0 flex-1">
          <h2 className={cn(TYPE.heading, 'text-ds-text')}>{title}</h2>
          {subtitle ? <p className={cn(TYPE.small, 'mt-s1 text-ds-muted')}>{subtitle}</p> : null}
        </div>
        {action ? <div className="shrink-0">{action}</div> : null}
      </div>
      {readOnly ? (
        <Banner tone="warning" className="mt-s4">
          {t('settings.read_only_hint')}
        </Banner>
      ) : null}
      <div className="mt-s5 min-w-0">{children}</div>
    </Card>
  );
}

export function SaveBar({
  canEdit,
  pending,
  onCancel,
  hint,
  disabled,
}: {
  canEdit: boolean;
  pending: boolean;
  onCancel?: () => void;
  hint?: ReactNode;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  if (!canEdit) return null;
  return (
    <div className="sticky bottom-0 z-10 -mx-s1 mt-s4 flex flex-col-reverse gap-s2 bg-gradient-to-t from-ds-bg from-70% to-transparent py-s4 nav:flex-row nav:items-center">
      {hint ? (
        <p className={cn(TYPE.small, 'flex items-center gap-s2 text-ds-muted')}>{hint}</p>
      ) : null}
      <div className="flex w-full flex-col-reverse gap-s2 nav:ml-auto nav:w-auto nav:flex-row nav:items-center">
        {onCancel ? (
          <Button
            type="button"
            variant="tertiary"
            className="w-full nav:w-auto"
            onClick={onCancel}
            disabled={pending}
          >
            {t('common.cancel')}
          </Button>
        ) : null}
        <Button
          type="submit"
          className="w-full nav:w-auto"
          loading={pending}
          loadingText={t('settings.saving')}
          disabled={disabled}
        >
          {t('settings.save')}
        </Button>
      </div>
    </div>
  );
}

export function FieldWithIcon({
  id,
  label,
  icon: Icon,
  value,
  onChange,
  type = 'text',
  disabled,
  hint,
  error,
  required,
  autoComplete,
}: {
  id: string;
  label: string;
  icon: LucideIcon;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  disabled?: boolean;
  hint?: string;
  error?: string;
  required?: boolean;
  autoComplete?: string;
}) {
  return (
    <Input
      id={id}
      name={id}
      type={type}
      label={label}
      value={value}
      autoComplete={autoComplete}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      hint={hint}
      error={error}
      required={required}
      leadingIcon={<Icon className="h-4 w-4" aria-hidden />}
    />
  );
}

export function PlainField({
  id,
  label,
  value,
  onChange,
  type = 'text',
  disabled,
  placeholder,
  hint,
  required,
  autoComplete,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  disabled?: boolean;
  placeholder?: string;
  hint?: string;
  required?: boolean;
  autoComplete?: string;
}) {
  return (
    <Input
      id={id}
      name={id}
      type={type}
      label={label}
      value={value}
      autoComplete={autoComplete}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      placeholder={placeholder}
      hint={hint}
      required={required}
    />
  );
}

export function SettingsLoading({ lines = 4 }: { lines?: number }) {
  return (
    <Card>
      <Skeleton className="h-6 w-48" />
      <div className="mt-s3">
        <SkeletonText lines={lines} />
      </div>
      <div className="mt-s5 grid grid-cols-1 gap-s4 nav:grid-cols-2">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    </Card>
  );
}

export function SettingsEmpty({
  icon,
  title,
  description,
  action,
}: {
  icon: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
}) {
  return <EmptyState icon={icon} title={title} description={description} action={action} />;
}

export function SettingsBackLink({
  to = '/settings/integrations',
  label,
}: {
  to?: string;
  label?: string;
}) {
  const { t } = useTranslation();
  return (
    <Link
      to={to}
      className={cn(TYPE.control, 'mb-s4 inline-flex items-center gap-s1 font-medium text-ds-teal')}
    >
      <ArrowLeft className="h-4 w-4" aria-hidden />
      {label ?? t('settings.integrations.back')}
    </Link>
  );
}

export function SettingsSubpageHeader({
  title,
  subtitle,
  icon,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  icon?: ReactNode;
}) {
  return (
    <div className="mb-s4 min-w-0">
      <div className="flex items-center gap-s2">
        {icon ? <span className="text-ds-teal">{icon}</span> : null}
        <h2 className={cn(TYPE.heading, 'text-ds-text')}>{title}</h2>
      </div>
      {subtitle ? <p className={cn(TYPE.body, 'mt-s1 text-ds-muted')}>{subtitle}</p> : null}
    </div>
  );
}

export function SettingsChoiceRow({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex min-w-0 items-start gap-s3 rounded-ctl border border-ds-line bg-ds-card p-s3',
        className,
      )}
    >
      {children}
    </div>
  );
}

export function SettingsHint({ children, className }: { children: ReactNode; className?: string }) {
  return <p className={cn(TYPE.small, 'text-ds-muted', className)}>{children}</p>;
}
